import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ### EmbedScale
 *
 * Measured fit of a fixed-width embed inside a fluid container.
 */
export type EmbedScale<TElement extends HTMLElement> = {
  /**
   * ### containerRef
   *
   * Attach to the element whose width the embed must fit. Measuring starts
   * when the element mounts and stops when it unmounts.
   */
  containerRef: (element: TElement | null) => void;

  /**
   * ### scale
   *
   * Factor to apply as `transform: scale(...)` on the embed. Never above `1`,
   * so a container wider than the embed leaves it at its design size instead
   * of stretching it. Before the first measurement it is `1`, which keeps the
   * server-rendered markup identical to the first client paint.
   */
  scale: number;
};

/**
 * ### useEmbedScale
 *
 * Tracks the factor that fits a fixed-width embed into its container, for an
 * embed that cannot reflow — a third-party console laid out for desktop, whose
 * markup this site does not control and cannot make responsive.
 *
 * Width is read from a `ResizeObserver` rather than from the viewport, because
 * the container is narrower than the viewport by the page gutter and changes
 * again at every breakpoint.
 *
 * @param designWidth Width in CSS pixels the embed is laid out for; must be above zero
 * @returns The container callback ref and the current scale
 * @see {@link EmbedScale} What each field is for
 */
export function useEmbedScale<TElement extends HTMLElement>(
  designWidth: number,
): EmbedScale<TElement> {
  const [scale, setScale] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  const containerRef = useCallback(
    (element: TElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (element === null || typeof ResizeObserver === 'undefined') {
        return;
      }

      const applyWidth = (width: number) => {
        if (width <= 0) {
          return;
        }

        setScale(Math.min(1, width / designWidth));
      };

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];

        if (entry === undefined) {
          return;
        }

        applyWidth(entry.contentRect.width);
      });

      observer.observe(element);
      observerRef.current = observer;
      applyWidth(element.getBoundingClientRect().width);
    },
    [designWidth],
  );

  return { containerRef, scale };
}
